import * as constants from "../../constants";
import fetch from "../util/api-ajax";
import { setErrorMessage, setSuccessMessage } from "./pageStatus";

const messageFrom = (err) => (err && err.message) || "Unexpected error";

function requestBatteryDevices() {
  return { type: constants.BATTERY_DEVICES_REQUEST };
}

function receiveBatteryDevices(payload) {
  return { type: constants.BATTERY_DEVICES_SUCCESS, payload };
}

function batteryDevicesFailure(error) {
  return { type: constants.BATTERY_DEVICES_FAILURE, error };
}

export function fetchBatteryDevices(memberId) {
  return (dispatch, getState) => {
    dispatch(requestBatteryDevices());
    const { limit } = getState().devices.battery_devices;
    fetch(constants.MEMBER_BATTERY_DEVICES_SUMMARY_URL, { limit, page: 1, member_id: memberId })
      .then((res) => dispatch(receiveBatteryDevices(res)))
      .catch((err) => {
        dispatch(batteryDevicesFailure(messageFrom(err)));
        dispatch(setErrorMessage(`Failed to load batteries: ${messageFrom(err)}`));
      });
  };
}

export function fetchMoreBatteryDevices(memberId) {
  return (dispatch, getState) => {
    const batteries = getState().devices.battery_devices;
    if (!batteries.hasNext || batteries.isFetchingMore) return;

    dispatch({ type: constants.BATTERY_DEVICES_FETCH_MORE_REQUEST });
    const { limit, next } = batteries;
    fetch(constants.MEMBER_BATTERY_DEVICES_SUMMARY_URL, { limit, page: next, member_id: memberId })
      .then((res) =>
        dispatch({ type: constants.BATTERY_DEVICES_FETCH_MORE_SUCCESS, payload: res })
      )
      .catch((err) => {
        dispatch(batteryDevicesFailure(messageFrom(err)));
        dispatch(setErrorMessage(`Failed to load more batteries: ${messageFrom(err)}`));
      });
  };
}

// ── Unassigned devices (assign-to-member popup) ──────────────────────────────

function unassignedDevicesFailure(error) {
  return { type: constants.UNASSIGNED_BATTERY_DEVICES_FAILURE, error };
}

export function resetUnassignedDevices() {
  return { type: constants.UNASSIGNED_BATTERY_DEVICES_RESET };
}

// Fetch the first page of unassigned devices. Pagination is cursor-based, so the
// first page is requested with an empty cursor.
export function fetchUnassignedDevices() {
  return (dispatch, getState) => {
    dispatch({ type: constants.UNASSIGNED_BATTERY_DEVICES_REQUEST });
    const { limit } = getState().devices.unassigned_battery_devices;
    fetch(constants.UNASSIGNED_BATTERY_DEVICES_URL, { limit, cursor: "" })
      .then((res) => dispatch({ type: constants.UNASSIGNED_BATTERY_DEVICES_SUCCESS, payload: res }))
      .catch((err) => {
        dispatch(unassignedDevicesFailure(messageFrom(err)));
        dispatch(setErrorMessage(`Failed to load unassigned devices: ${messageFrom(err)}`));
      });
  };
}

export function fetchMoreUnassignedDevices() {
  return (dispatch, getState) => {
    const unassigned = getState().devices.unassigned_battery_devices;
    if (!unassigned.hasNext || unassigned.isFetchingMore) return;

    dispatch({ type: constants.UNASSIGNED_BATTERY_DEVICES_FETCH_MORE_REQUEST });
    const { limit, cursor } = unassigned;
    fetch(constants.UNASSIGNED_BATTERY_DEVICES_URL, { limit, cursor })
      .then((res) =>
        dispatch({ type: constants.UNASSIGNED_BATTERY_DEVICES_FETCH_MORE_SUCCESS, payload: res })
      )
      .catch((err) => {
        dispatch(unassignedDevicesFailure(messageFrom(err)));
        dispatch(setErrorMessage(`Failed to load more unassigned devices: ${messageFrom(err)}`));
      });
  };
}

// Assign the selected devices to the member in context. The AssignBatteryToMember
// endpoint takes the full list of device ids and assigns them as a single batch;
// it is all-or-nothing, so either every id succeeds or the whole request fails.
// Resolves with a { succeeded, failed } summary so the popup can report results,
// and refreshes the member's battery summary table on success.
export function assignDevicesToMember(memberId, deviceIds) {
  return async (dispatch) => {
    dispatch({ type: constants.ASSIGN_BATTERY_DEVICES_REQUEST });

    try {
      await fetch(constants.ASSIGN_BATTERY_TO_MEMBER_URL, {
        device_ids: deviceIds,
        member_id: memberId,
      });

      dispatch({ type: constants.ASSIGN_BATTERY_DEVICES_SUCCESS });
      dispatch(
        setSuccessMessage(`Assigned ${deviceIds.length} device${deviceIds.length > 1 ? "s" : ""} to member`)
      );
      dispatch(fetchBatteryDevices(memberId));

      return { succeeded: [...deviceIds], failed: [] };
    } catch (err) {
      const error = messageFrom(err);
      const summary = `Failed to assign ${deviceIds.length} device${deviceIds.length > 1 ? "s" : ""}`;
      dispatch({ type: constants.ASSIGN_BATTERY_DEVICES_FAILURE, error: summary });
      dispatch(setErrorMessage(`${summary}: ${error}`));

      return { succeeded: [], failed: deviceIds.map((deviceId) => ({ deviceId, error })) };
    }
  };
}
